import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Button, Spinner, Alert, Table, Collapse } from 'react-bootstrap';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { toast } from 'sonner';
import {
  ArrowLeft,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Scale,
  Sparkles,
} from 'lucide-react';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';
import { brandSelectTheme } from '../../Common/reactSelectTheme';
import {
  formatDate,
  formatNumber,
  getAssetAccountingUnavailable,
  round2,
  unwrapData,
} from './assetUtils';

const num = (value) => parseFloat(value) || 0;

/**
 * TaxStep — bokslutssteget för räkenskapsenlig avskrivning (plan §9 fas 2,
 * research §2.5 dual track + §4.3 Tier 3). Preview-first: allt hämtas från
 * GET /assets/tax/preview och inget bokförs förrän användaren bekräftat via
 * Swal. Gating sker server-side — en 403 FEATURE_NOT_AVAILABLE renderas som
 * uppgraderingskort så att funktionen går att upptäcka utan route-gate.
 */
const TaxStep = () => {
  const { companyId } = useParams();
  const history = useHistory();

  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [loadingYears, setLoadingYears] = useState(true);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [upgrade, setUpgrade] = useState(null); // 403-payload → uppgraderingskort
  const [error, setError] = useState(null);
  const [showCalculation, setShowCalculation] = useState(false);
  const [posting, setPosting] = useState(false);

  // Samma hämtning som rapporterna (BalanceReport-mönstret).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await axios.get(
          getApiUrl(`${companyId}/accounting/fiscalyears`),
          { withCredentials: true }
        );
        const years = result.data?.fiscalYears || [];
        if (cancelled) return;
        const sorted = [...years].sort((a, b) => b.year - a.year);
        setFiscalYears(sorted);
        if (sorted.length > 0) {
          // Bokslutssteget gäller oftast det senast avslutade året — välj det
          // om det finns, annars det senaste.
          const today = new Date();
          const ended = sorted.find((fy) => new Date(fy.end_date) <= today);
          setSelectedYear(ended || sorted[0]);
        }
      } catch (err) {
        if (!cancelled) setError('Kunde inte hämta räkenskapsår.');
      } finally {
        if (!cancelled) setLoadingYears(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const fetchPreview = useCallback(async (fiscalYearId) => {
    setLoadingPreview(true);
    setError(null);
    setUpgrade(null);
    setPreview(null);
    try {
      const response = await axios.get(
        getApiUrl(`${companyId}/assets/tax/preview?fiscalYearId=${fiscalYearId}`),
        { withCredentials: true }
      );
      setPreview(unwrapData(response));
    } catch (err) {
      const data = err.response?.data;
      if (err.response?.status === 403 && data?.error === 'FEATURE_NOT_AVAILABLE') {
        setUpgrade(data);
      } else {
        const unavailable = getAssetAccountingUnavailable(err);
        setError(unavailable?.message || (data?.error && data.error !== 'FEATURE_NOT_AVAILABLE'
          ? data.error
          : 'Kunde inte hämta den skattemässiga beräkningen. Försök igen senare.'));
      }
    } finally {
      setLoadingPreview(false);
    }
  }, [companyId]);

  useEffect(() => {
    if (selectedYear?.fiscal_year_id || selectedYear?.id) {
      fetchPreview(selectedYear.fiscal_year_id ?? selectedYear.id);
    }
  }, [selectedYear, fetchPreview]);

  const yearOptions = useMemo(() => fiscalYears.map((fy) => ({
    value: fy.fiscal_year_id ?? fy.id,
    label: `${fy.year} (${formatDate(fy.start_date)} – ${formatDate(fy.end_date)})`,
    fiscalYear: fy,
  })), [fiscalYears]);

  const selectedOption = yearOptions.find(
    (o) => o.value === (selectedYear?.fiscal_year_id ?? selectedYear?.id)
  ) || null;

  // Defensivt uppackade värden — backend-kontraktet byggs ut parallellt.
  const pool = preview?.pool || {};
  const book = preview?.book || {};
  const isK1 = preview?.mode === 'k1';
  const mainValue = num(pool.mainRuleValue);
  const supplValue = num(pool.supplRuleValue);
  const chosenValue = num(pool.chosenTaxValue);
  const delta = num(preview?.overdepreciationDelta);
  const postingRows = preview?.postingPreview?.rows || [];
  const conformity = preview?.conformity;
  const alreadyPosted = Boolean(preview?.alreadyPosted);
  const mainIsChosen = round2(mainValue) === round2(chosenValue);

  const handlePost = async () => {
    const fiscalYearId = selectedYear?.fiscal_year_id ?? selectedYear?.id;
    if (!fiscalYearId) return;
    const result = await Swal.fire({
      title: isK1 ? 'Bokför årets avskrivning?' : 'Bokför överavskrivning?',
      text: isK1
        ? `Årets avskrivning på ${formatNumber(num(pool.taxDepreciation))} kr bokförs för räkenskapsåret ${selectedYear?.year || ''}.`
        : `Ett verifikat på ${formatNumber(delta)} kr skapas (överavskrivning, konto 8853/2153) för räkenskapsåret ${selectedYear?.year || ''}.`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#44814E',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Ja, bokför',
      cancelButtonText: 'Avbryt',
    });
    if (!result.isConfirmed) return;

    setPosting(true);
    try {
      await axios.post(
        getApiUrl(`${companyId}/assets/tax/post`),
        { fiscalYearId },
        { withCredentials: true }
      );
      toast.success(isK1 ? 'Årets avskrivning är bokförd.' : 'Överavskrivningen är bokförd.');
      await fetchPreview(fiscalYearId);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Kunde inte bokföra. Försök igen.');
    } finally {
      setPosting(false);
    }
  };

  const renderUpgradeCard = () => {
    const planName = upgrade?.plan || upgrade?.planName || upgrade?.requiredPlan || 'ett större paket';
    const upgradeTo = upgrade?.upgradeUrl && String(upgrade.upgradeUrl).startsWith('/')
      ? upgrade.upgradeUrl
      : `/${companyId}/settings/subscription`;
    return (
      <div className="modern-card">
        <div className="text-center" style={{ padding: '3rem 2rem' }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: 16,
              background: 'var(--rgba-primary-1)',
              color: 'var(--primary)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem',
            }}
          >
            <Sparkles size={26} />
          </div>
          <h5 style={{ fontWeight: 700, color: 'var(--title)' }}>
            Skatteoptimering ingår i {planName} — uppgradera
          </h5>
          <p className="text-muted mx-auto" style={{ maxWidth: 520, fontSize: '0.9rem', lineHeight: 1.6 }}>
            Med skatteoptimeringen räknar LedgerLoop ut hur mycket snabbare företaget
            får skriva av skattemässigt (30-regeln och 20-regeln) och bokför
            överavskrivningen åt dig i bokslutet.
          </p>
          <div className="d-flex justify-content-center gap-2 flex-wrap">
            <Button variant="primary" onClick={() => history.push(upgradeTo)}>
              Uppgradera
            </Button>
            <Button variant="outline-primary" onClick={() => history.push(`/${companyId}/ai-assistant`)}>
              Fråga Finn
            </Button>
          </div>
          <p className="text-muted mt-3 mb-0" style={{ fontSize: '0.8125rem' }}>
            Din redovisningskonsult kan också hjälpa dig med det här steget.
          </p>
        </div>
      </div>
    );
  };

  // Regeljämförelsen: 30-regeln och 20-regeln sida vid sida, lägsta värdet
  // markerat (lägre skattemässigt värde = större avdrag).
  const renderRuleTile = (title, value, explanation, isChosen) => (
    <div
      style={{
        flex: '1 1 240px',
        border: isChosen ? '2px solid var(--primary)' : '1px solid #e2e8f0',
        background: isChosen ? 'var(--rgba-primary-1)' : '#fff',
        borderRadius: 12,
        padding: '0.9rem 1.1rem',
      }}
    >
      <div className="d-flex align-items-center justify-content-between gap-2">
        <span style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--title)' }}>{title}</span>
        {isChosen && (
          <span
            style={{
              background: 'var(--primary)',
              color: '#fff',
              borderRadius: 999,
              padding: '1px 10px',
              fontSize: '0.7rem',
              fontWeight: 700,
            }}
          >
            Lägst — väljs
          </span>
        )}
      </div>
      <div style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--title)', margin: '4px 0' }}>
        {formatNumber(value, 0)} kr
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--secondary)', lineHeight: 1.5 }}>{explanation}</div>
    </div>
  );

  const renderPreview = () => (
    <>
      {conformity && conformity.ok === false && (
        <Alert variant="warning">{conformity.message || 'Kravet på överensstämmelse mellan bokföring och deklaration (räkenskapsenlig avskrivning) riskerar att brytas.'}</Alert>
      )}

      {/* Förklaring i klarspråk FÖRE siffrorna (progressive disclosure Tier 3). */}
      <div
        style={{
          background: 'var(--rgba-primary-1)',
          border: '1px solid var(--rgba-primary-2)',
          borderRadius: 12,
          padding: '1rem 1.25rem',
          marginBottom: '1.25rem',
          fontSize: '0.9375rem',
          color: 'var(--title)',
          lineHeight: 1.6,
        }}
      >
        {isK1 ? (
          <>
            För enskild firma hanteras de skattemässiga avskrivningarna i NE-bilagan
            vid deklarationen — siffrorna nedan är underlaget. Bokföring av
            justeringen stöds inte ännu; din redovisningskonsult kan hjälpa dig,
            eller fråga Finn.
          </>
        ) : (
          <>
            Skattemässigt får företaget skriva av snabbare än den planenliga
            avskrivningen. Skillnaden bokförs som en obeskattad reserv
            (överavskrivning) — du skjuter upp skatt utan att den vanliga
            avskrivningsplanen ändras.
          </>
        )}
      </div>

      {/* Jämförelsen: planenligt UB vs skattemässigt lägsta värde. */}
      <div className="modern-card">
        <div style={{ padding: '1.25rem 1.5rem' }}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <Scale size={18} style={{ color: 'var(--primary)' }} />
            <h5 className="mb-0" style={{ fontWeight: 600 }}>Planenligt vs skattemässigt</h5>
          </div>

          <div className="d-flex flex-wrap gap-3 mb-3">
            <div style={{ flex: '1 1 200px' }}>
              <div style={{ fontSize: '0.75rem', color: 'var(--secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                Planenligt bokfört värde (UB)
              </div>
              <div style={{ fontSize: '1.35rem', fontWeight: 700, color: 'var(--title)' }}>
                {formatNumber(num(book.bookValueUB), 0)} kr
              </div>
              <div style={{ fontSize: '0.78rem', color: 'var(--secondary)' }}>
                Årets planenliga avskrivning: {formatNumber(num(book.plannedDepreciation), 0)} kr
              </div>
            </div>
          </div>

          <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginBottom: 8 }}>
            Skattemässigt lägsta värde — det lägsta av de två reglerna får användas:
          </div>
          <div className="d-flex flex-wrap gap-3">
            {renderRuleTile(
              '30-regeln (huvudregeln)',
              mainValue,
              '70 % av underlaget: ingående skattemässigt värde plus årets inköp, minus sålda tillgångar.',
              mainIsChosen
            )}
            {renderRuleTile(
              '20-regeln (kompletteringsregeln)',
              supplValue,
              'Anskaffningsvärdena skrivs av rakt med 20 % per år — allt är avskrivet efter fem år.',
              !mainIsChosen
            )}
          </div>

          <Button
            variant="link"
            size="sm"
            style={{ textDecoration: 'none', color: 'var(--primary)', paddingLeft: 0 }}
            onClick={() => setShowCalculation((prev) => !prev)}
          >
            {showCalculation ? 'Dölj beräkningen' : 'Visa beräkningen'}{' '}
            {showCalculation ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
          <Collapse in={showCalculation}>
            <div>
              <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.75rem 1rem', fontSize: '0.8125rem' }}>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Ingående skattemässigt värde</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(num(pool.openingTaxValue))} kr</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">+ Årets inköp</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(num(pool.additions))} kr</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">− Sålda tillgångar (ersättning)</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(num(pool.proceedsDeduction))} kr</span>
                </div>
                <div className="d-flex justify-content-between" style={{ borderTop: '1px solid #e2e8f0', marginTop: 4, paddingTop: 4 }}>
                  <span className="text-muted">= Underlag</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(num(pool.underlag))} kr</span>
                </div>
                <div className="d-flex justify-content-between mt-2">
                  <span className="text-muted">Valt skattemässigt värde</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(chosenValue)} kr</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Skattemässig avskrivning i år</span>
                  <span style={{ fontWeight: 600 }}>{formatNumber(num(pool.taxDepreciation))} kr</span>
                </div>
              </div>
            </div>
          </Collapse>
        </div>
      </div>

      {/* Avdraget + verifikatförhandsvisning + bokför-knapp. */}
      <div className="modern-card">
        <div style={{ padding: '1.25rem 1.5rem' }}>
          {alreadyPosted ? (
            <Alert variant="success" className="d-flex align-items-center gap-2 mb-3">
              <CheckCircle2 size={18} />
              <span>
                {isK1
                  ? 'Årets avskrivning är redan bokförd för det här räkenskapsåret.'
                  : 'Överavskrivningen är redan bokförd för det här räkenskapsåret.'}
              </span>
            </Alert>
          ) : (
            <div style={{ fontSize: '1rem', color: 'var(--title)', marginBottom: '0.75rem' }}>
              {isK1 ? (
                <>Årets avskrivning: <strong>{formatNumber(num(pool.taxDepreciation), 0)} kr</strong></>
              ) : delta > 0 ? (
                <>Möjligt extra avdrag i år: <strong>{formatNumber(delta, 0)} kr</strong></>
              ) : (
                <span className="text-muted">
                  Ingen ytterligare överavskrivning är möjlig i år — den planenliga
                  avskrivningen ligger redan på eller under det skattemässiga
                  lägsta värdet.
                </span>
              )}
            </div>
          )}

          {postingRows.length > 0 && (
            <>
              <div style={{ fontSize: '0.8125rem', color: 'var(--secondary)', marginBottom: 6 }}>
                {alreadyPosted ? 'Så här bokfördes det:' : 'Så här bokförs det:'}
              </div>
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden', marginBottom: '1rem' }}>
                <Table size="sm" className="mb-0 asset-tax-table">
                  <thead>
                    <tr>
                      <th>Konto</th>
                      <th className="text-end">Debet</th>
                      <th className="text-end">Kredit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {postingRows.map((row, index) => (
                      <tr key={index}>
                        <td>{row.account}</td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.type === 'debit' ? `${formatNumber(row.amount)} kr` : ''}
                        </td>
                        <td className="text-end" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {row.type === 'credit' ? `${formatNumber(row.amount)} kr` : ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </>
          )}

          {!alreadyPosted && preview?.postingSupported !== false && (
            <Button
              variant="primary"
              onClick={handlePost}
              disabled={posting || postingRows.length === 0 || delta <= 0}
            >
              {posting ? 'Bokför…' : 'Bokför överavskrivning'}
            </Button>
          )}
        </div>
      </div>

      {/* Fastigheter — utanför 30/20-poolen (byggnader IL 19 kap., mark-
          anläggningar IL 20 kap., mark skrivs inte av). Informativt underlag;
          bokförd plan = skattemässigt avdrag när planen följer Skatteverkets
          procentsatser (BFNAR 2017:3 p. 9.31 / K2 10.31). */}
      {Array.isArray(preview.properties) && preview.properties.length > 0 && (
        <div className="modern-card mt-3">
          <div style={{ padding: '1.5rem 2rem' }}>
            <h5 className="mb-1" style={{ fontWeight: 600 }}>Fastigheter</h5>
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
              Byggnader och mark ingår inte i 30/20-reglerna ovan. Avdraget följer
              procentsatsen per byggnadstyp — när avskrivningsplanen följer den behövs
              ingen skattemässig justering.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <Table size="sm" className="mb-0 asset-tax-table">
                <thead>
                  <tr>
                    <th>Tillgång</th>
                    <th className="text-end">Anskaffningsvärde</th>
                    <th className="text-end">Procent per år</th>
                    <th className="text-end">Årets avskrivning</th>
                    <th className="text-end">Ack. avskrivningar</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.properties.map((p) => (
                    <tr key={p.asset_id}>
                      <td>{p.name}</td>
                      <td className="text-end">{formatNumber(num(p.acquisition_value), 0)} kr</td>
                      <td className="text-end">{p.depreciable ? `${p.annualPercent} %` : 'Skrivs inte av'}</td>
                      <td className="text-end">{formatNumber(num(p.yearDepreciation), 0)} kr</td>
                      <td className="text-end">{formatNumber(num(p.accumulatedDepreciation), 0)} kr</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Till deklarationen — INK2-rutor + SRU-koder (SKV 269). */}
      {preview.deklaration && (
        <div className="modern-card mt-3">
          <div style={{ padding: '1.5rem 2rem' }}>
            <h5 className="mb-1" style={{ fontWeight: 600 }}>Till deklarationen (INK2)</h5>
            <p className="text-muted" style={{ fontSize: '0.8125rem', marginBottom: '0.75rem' }}>
              Så här hamnar siffrorna i inkomstdeklarationen. SRU-koderna gäller vid
              filinlämning.
            </p>
            <div style={{ overflowX: 'auto' }}>
              <Table size="sm" className="mb-0 asset-tax-table">
                <thead>
                  <tr>
                    <th style={{ width: 90 }}>Ruta</th>
                    <th>Post</th>
                    <th className="text-end" style={{ width: 110 }}>SRU-kod</th>
                    <th className="text-end" style={{ width: 140 }}>Belopp</th>
                  </tr>
                </thead>
                <tbody>
                  {[...preview.deklaration.ink2r.map((r) => ({ ...r, form: 'INK2R' })),
                    ...preview.deklaration.ink2s.map((r) => ({ ...r, form: 'INK2S' }))]
                    .filter((r) => num(r.amount) !== 0)
                    .map((r) => (
                      <tr key={`${r.form}-${r.row}`}>
                        <td>{r.form} {r.row}</td>
                        <td>{r.label}{r.sign ? ` (${r.sign})` : ''}</td>
                        <td className="text-end asset-num">{r.sru}</td>
                        <td className="text-end asset-num">{formatNumber(num(r.amount), 0)} kr</td>
                      </tr>
                    ))}
                </tbody>
              </Table>
            </div>
            {preview.deklaration.note && (
              <p className="text-muted mb-0 mt-2" style={{ fontSize: '0.75rem' }}>
                {preview.deklaration.note}
              </p>
            )}
          </div>
        </div>
      )}
    </>
  );

  if (loadingYears) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: 400 }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2 text-muted">Laddar räkenskapsår…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-3">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <div className="d-flex align-items-center gap-3">
          <Button
            variant="outline-secondary"
            size="sm"
            className="d-flex align-items-center gap-1"
            onClick={() => history.push(`/${companyId}/assets`)}
          >
            <ArrowLeft size={14} /> Tillgångar
          </Button>
          <div>
            <h3 style={{ fontWeight: 700, color: 'var(--title)', marginBottom: 2 }}>
              Skattemässig avskrivning
            </h3>
            <p className="text-muted mb-0" style={{ fontSize: '0.875rem' }}>
              Bokslutssteg — jämför den planenliga avskrivningen med vad som får dras av skattemässigt.
            </p>
          </div>
        </div>
        <div style={{ minWidth: 280 }}>
          <Select
            options={yearOptions}
            value={selectedOption}
            onChange={(option) => setSelectedYear(option ? option.fiscalYear : null)}
            placeholder="Välj räkenskapsår"
            theme={brandSelectTheme}
            isSearchable={false}
          />
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {fiscalYears.length === 0 && !error && (
        <p className="text-muted">
          Inga räkenskapsår hittades — skapa ett räkenskapsår under Inställningar först.
        </p>
      )}

      {upgrade && renderUpgradeCard()}

      {loadingPreview && (
        <div className="d-flex justify-content-center py-5">
          <Spinner animation="border" variant="primary" />
        </div>
      )}

      {!loadingPreview && !upgrade && preview && renderPreview()}

      <style>{`
        .asset-tax-table thead th {
          background: #f9fafb;
          color: #6b7280;
          font-size: 0.6875rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          border-bottom: 1px solid #e5e7eb;
        }
        .asset-tax-table tbody td { border-color: #f3f4f6; }
      `}</style>
    </div>
  );
};

export default TaxStep;
